import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Checkbox } from '@/design-system/checkbox';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { hubsAPI, type Hub } from '@/services/api/hubs';
import {
  COD_FINANCE_ISSUE_REASON_LABELS,
  COD_NO_HUB as NO_HUB,
  type CodFinanceBreakdownRow,
  type CodFinanceDepositBatch,
  type CodFinanceOrder,
  type CodFinanceQueue,
  getCodFinanceQueue,
} from '@/services/api/codOrders';
import { formatRupiah } from './CodBadges';
import {
  DateRangeFilter,
  rangeForPreset,
  type DateRangeValue,
} from './CodFilters';
import { downloadCodFinanceCsv } from './codExport';
import {
  ConfirmFinanceModal,
  FinanceIssueModal,
  MatchDepositModal,
  type CodFinanceSelection,
} from './CodFinanceModals';

/**
 * The Finance verification queue — Leg 3.
 *
 * The board answers "where is this order's money". The per-rider view answers
 * "how much is this rider holding". This one answers the question Finance
 * actually asks, which is neither: **"does this line on the bank statement
 * match what Ops says they deposited?"**
 *
 * So the grouping is the DEPOSIT, not the hub or the rider. One PIC transfer is
 * one statement line covering many orders, and that is the unit Finance matches
 * and confirms. Hub and client are headings and filters over it.
 *
 * THE RULE THAT SHAPES EVERYTHING HERE: a filter never changes what a batch
 * contains. Filtering to one client still shows the full transfer total, with
 * the out-of-scope rows dimmed, because a filtered subtotal is a number that
 * appears on no statement — and Finance matching against it would either chase a
 * shortfall that does not exist or confirm half a transfer and strand the rest.
 */

const EMPTY: CodFinanceQueue = {
  kpi: {
    awaitingAmount: 0,
    awaitingOrders: 0,
    awaitingDeposits: 0,
    matchedOrders: 0,
    confirmedAmount: 0,
    confirmedOrders: 0,
    flaggedOrders: 0,
    staleOrders: 0,
    staleAmount: 0,
    oldestDepositAgeDays: null,
  },
  breakdownByClient: [],
  breakdownByHub: [],
  batches: [],
  totalDeposits: 0,
  hasMore: false,
};

/**
 * A KPI tile. Fixed in number — never one per client.
 *
 * Carries the prototype's left tone rail: four white cards side by side are
 * indistinguishable at a glance, and these four are not equal — one is money
 * waiting, one is money settled, one is a problem count.
 */
const Kpi: React.FC<{
  icon: string;
  label: string;
  value: string;
  hint: string;
  tone?: 'warning' | 'success' | 'error';
}> = ({ icon, label, value, hint, tone }) => (
  <div
    className={`rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4 ${
      tone === 'warning'
        ? 'border-l-[3px] border-l-warning-base'
        : tone === 'success'
          ? 'border-l-[3px] border-l-success-base'
          : tone === 'error'
            ? 'border-l-[3px] border-l-error-base'
            : 'border-l-[3px] border-l-stroke-sub-300'
    }`}
  >
    <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-text-sub-600">
      <i className={`${icon} text-sm`} />
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-2 truncate text-xl font-medium tabular-nums leading-7">
      {value}
    </div>
    <div className="mt-0.5 truncate text-[11.5px] text-text-sub-600">
      {hint}
    </div>
  </div>
);

/**
 * One order inside a deposit, as a table row.
 *
 * A table and not a card stack, matching the prototype: Finance reads these
 * column-wise — "which of these is not matched yet", "who marked that one" —
 * and a stack of cards makes every such question a scan instead of a glance.
 */
const OrderRow: React.FC<{
  order: CodFinanceOrder;
  selected: boolean;
  onToggleSelect: () => void;
  onMatch: (order: CodFinanceOrder) => void;
  onFlag: (order: CodFinanceOrder) => void;
  onOpen: (order: CodFinanceOrder) => void;
}> = ({ order, selected, onToggleSelect, onMatch, onFlag, onOpen }) => (
  <tr
    className={
      // Dimmed, not hidden: the batch total counts it, so removing it would
      // leave a total nothing on screen adds up to.
      `${order.filteredOut ? 'opacity-40' : ''} cursor-pointer hover:bg-bg-weak-50`
    }
    // Membuka detail COD pesanan ini. Klik pada checkbox dan tombol aksi
    // dihentikan di elemennya sendiri, supaya mencentang satu baris tidak
    // ikut membuka panel — dua niat berbeda di satu baris.
    onClick={() => onOpen(order)}
  >
    <td className="px-3 py-2.5">
      {/* Per ORDER, not only per deposit. Finance settles part of a transfer
          routinely — three of five on today's statement, two not — and with
          only a batch-level checkbox the answer was "open each one and act on
          it singly". A flagged order is excluded from both bulk actions, so it
          cannot be picked. */}
      <span onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          disabled={!!order.financeIssueReason}
          title="Pilih pesanan ini untuk dicocokkan / dikonfirmasi sekaligus"
        />
      </span>
    </td>
    <td className="px-3 py-2.5">
      <span className="font-mono text-[11px]">{order.bookingId}</span>
      {order.deliveryUid ? (
        <div className="font-mono text-[10px] text-text-soft-400">
          {order.deliveryUid}
        </div>
      ) : null}
    </td>
    <td className="px-3 py-2.5">
      <Badge status="information" size="sm">
        {order.clientName ?? '—'}
      </Badge>
    </td>
    <td className="px-3 py-2.5 text-xs">
      {order.riderName ?? '—'}
      <div className="text-[10.5px] text-text-soft-400">
        {order.hubName ?? 'Tanpa hub'}
      </div>
    </td>
    {/* Date over time, like the rider over the hub beside it. Numeric and on
        `px-2` because the width is the constraint: this table was measured to
        fit 1024 wide with no scrollbar (see the `min-w` below), and a
        `30 Aug 2026` column (94px) pushed it 20px past its scroller. `30/08/26`
        is 69px and fits again. */}
    <td className="whitespace-nowrap px-2 py-2.5 text-xs">
      {order.completedAt ? (
        <>
          {dayjs(order.completedAt).format('DD/MM/YY')}
          <div className="text-[10.5px] text-text-soft-400">
            {dayjs(order.completedAt).format('HH:mm')}
          </div>
        </>
      ) : (
        <span className="text-text-soft-400">—</span>
      )}
    </td>
    <td className="px-3 py-2.5 text-right tabular-nums">
      {formatRupiah(order.depositedAmount)}
    </td>
    <td className="px-3 py-2.5">
      {order.financeIssueReason ? (
        <Badge status="error" size="sm" title={order.financeIssueNote ?? ''}>
          {COD_FINANCE_ISSUE_REASON_LABELS[order.financeIssueReason]}
        </Badge>
      ) : order.eligible ? (
        <Badge status="success" size="sm">
          Cocok
        </Badge>
      ) : (
        <Badge status="faded" size="sm">
          Belum dicek
        </Badge>
      )}
    </td>
    <td className="px-3 py-2.5">
      {order.financeMatchedBy ? (
        <>
          {/* `break-all`: this is the column the table gives up width on at
              1024, and an address has no spaces to wrap at — without it the
              cell pushes the table wider instead of yielding. */}
          <div className="break-all font-mono text-[10.5px]">
            {order.financeMatchedBy}
          </div>
          {order.financeMatchedAt ? (
            <div className="text-[10px] text-text-soft-400">
              {dayjs(order.financeMatchedAt).format('DD MMM · HH:mm')}
            </div>
          ) : null}
        </>
      ) : (
        <span className="text-xs text-text-soft-400">—</span>
      )}
    </td>
    <td className="px-3 py-2.5 text-right">
      <div
        className="flex justify-end gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The prototype offers this per row as "Tandai cocok", which there is
            a bare flag with no reference. Here it opens the same modal the
            deposit-level action does, scoped to this one order — the reference
            is the evidence, and a per-row shortcut around it would be the one
            thing this feature refuses to ship. */}
        {!order.financeIssueReason && (
          <Button style="stroke" size="xs" onClick={() => onMatch(order)}>
            {order.financeBankRef ? 'Ubah ref' : 'Cocokkan'}
          </Button>
        )}
        <Button style="stroke" size="xs" onClick={() => onFlag(order)}>
          {order.financeIssueReason ? 'Ubah' : 'Bermasalah'}
        </Button>
      </div>
    </td>
  </tr>
);

/**
 * One deposit = one bank statement line. Always shown whole.
 *
 * EXPANDED BY DEFAULT, per the prototype. Finance opens this screen to read
 * what is inside each transfer — collapsing by default made the first action on
 * every card "open it", and a queue of twelve meant twelve clicks before any
 * work started. The chevron is there to fold away the ones already dealt with,
 * which is the rarer need and so the one that costs a click.
 */
const BatchCard: React.FC<{
  batch: CodFinanceDepositBatch;
  /** Ids of THIS batch's orders that are currently ticked. */
  selectedIds: Set<string>;
  expanded: boolean;
  /** Ticks or clears every selectable order in this batch. */
  onToggleAll: (on: boolean) => void;
  onToggleOrder: (orderId: string) => void;
  onToggleExpand: () => void;
  onMatch: () => void;
  onMatchOrder: (order: CodFinanceOrder) => void;
  onConfirm: () => void;
  onFlag: (order: CodFinanceOrder) => void;
  onOpenOrder: (order: CodFinanceOrder) => void;
}> = ({
  batch,
  selectedIds,
  expanded,
  onToggleAll,
  onToggleOrder,
  onToggleExpand,
  onMatch,
  onMatchOrder,
  onConfirm,
  onFlag,
  onOpenOrder,
}) => {
  const ready = batch.matchedOrders > 0;
  const matchable = batch.totalOrders - batch.flaggedOrders;
  // Selectable when there is ANY bulk action for it — matching or confirming.
  // Gating on `ready` alone made the unmatched deposits, which are exactly the
  // ones a bulk match is for, the only ones that could not be selected.
  const selectable = ready || matchable > 0;
  const filtered = batch.visibleOrders < batch.totalOrders;
  const allMatched = batch.matchedOrders === batch.totalOrders;
  // The header box is now a select-all over the rows, so its state has to be
  // derived from them — otherwise ticking rows by hand leaves the header
  // claiming the deposit is unselected while the bar counts its orders.
  const pickable = batch.orders.filter((o) => !o.financeIssueReason);
  const pickedHere = pickable.filter((o) => selectedIds.has(o.id)).length;
  const allPicked = pickable.length > 0 && pickedHere === pickable.length;

  return (
    <div className="overflow-hidden rounded-xl border border-stroke-soft-200 bg-bg-white-0">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <Checkbox
          // Three states, because two cannot say "some of this deposit". Radix
          // carries the third through `checked` itself; rendering a partial
          // pick as unchecked made a hand-picked subset look like nothing was
          // selected at all.
          checked={allPicked ? true : pickedHere > 0 ? 'indeterminate' : false}
          onCheckedChange={() => onToggleAll(!allPicked)}
          disabled={!selectable || pickable.length === 0}
          title="Pilih semua pesanan di setoran ini"
        />

        {/* Its own control, not the whole header: the header carries a
            checkbox and two buttons, and a click target wrapping all of them
            would fold the card when someone meant to press one. */}
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? 'Tutup daftar pesanan' : 'Buka daftar pesanan'}
          className="flex size-6 shrink-0 items-center justify-center rounded text-text-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950"
        >
          <i
            className={`text-base ${expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}
          />
        </button>

        <div className="min-w-0 flex-1">
          {/* Wraps, never truncates. This email IS the card's identity — which
              PIC's transfer this is — and `truncate` cut it to "Setoran · ko…"
              on a narrow window, leaving two characters to tell two PICs
              apart. `break-all` because an address has no spaces to break at,
              so without it a long one just overflows instead. */}
          <div className="text-label-small break-all">
            Setoran ·{' '}
            <span className="font-mono text-xs">{batch.depositedBy}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-text-sub-600">
            <span>{batch.depositedOn}</span>
            <span>·</span>
            <span>{batch.method}</span>
            <span>·</span>
            <span>{batch.totalOrders} pesanan</span>
            {batch.clientNames.map((c) => (
              <Badge key={c} status="information" size="sm">
                {c}
              </Badge>
            ))}
            {batch.hubNames.length > 1 && (
              <span>· {batch.hubNames.length} hub</span>
            )}
            {batch.financeBankRef && (
              <Badge status="success" size="sm">
                ref {batch.financeBankRef}
              </Badge>
            )}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-label-small tabular-nums">
            {formatRupiah(batch.totalAmount)}
          </div>
          <div className="mt-0.5 text-xs">
            {allMatched ? (
              <Badge status="success" size="sm">
                Semua cocok
              </Badge>
            ) : (
              <Badge status="faded" size="sm">
                {batch.matchedOrders}/{batch.totalOrders} cocok
              </Badge>
            )}
            {/* The batch stays whole under a filter; this says how much of it
                the current selection would have shown. */}
            {filtered && (
              <span className="ml-1.5 text-text-soft-400">
                menampilkan {batch.visibleOrders} dari {batch.totalOrders}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          {matchable > 0 && (
            <Button style="stroke" size="sm" onClick={onMatch}>
              {batch.financeBankRef ? 'Ubah ref mutasi' : 'Cocokkan dgn mutasi'}
            </Button>
          )}
          <Button size="sm" disabled={!ready} onClick={onConfirm}>
            Confirmed by Finance
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="overflow-x-auto border-t border-stroke-soft-200">
          {/* 780, and that number is measured, not chosen. The smallest desktop
              we support is 1024 wide; with the rail expanded the page hands
              this scroller 814px. The table's natural width — nothing wrapping
              — is 857, so it CANNOT fit at 1024 whatever we do, and the only
              question is whether it wraps or grows a horizontal scrollbar.
              780 keeps 1024 scrollbar-free in both rail states, and the one
              column that has to give is the operator email, which wraps
              cleanly below. (720 was too tight and crushed every cell; 860
              overflowed 1024 by 46px.) */}
          <table className="w-full min-w-[780px] text-xs">
            <thead>
              <tr className="bg-bg-weak-50 text-text-soft-400">
                <th className="w-9 px-3 py-2" />
                <th className="px-3 py-2 text-left font-medium">Booking</th>
                <th className="px-3 py-2 text-left font-medium">Client</th>
                <th className="px-3 py-2 text-left font-medium">Rider · Hub</th>
                <th className="px-2 py-2 text-left font-medium">
                  Tanggal selesai
                </th>
                <th className="px-3 py-2 text-right font-medium">Disetor</th>
                <th className="px-3 py-2 text-left font-medium">Cocok?</th>
                {/* Names the ACTION, not a generic "marked". The cell holds
                    `financeMatchedBy` — who tied this order to a bank line —
                    but the row's other action is "Bermasalah", which also
                    marks something, so "Ditandai oleh" read as the person who
                    flagged it. Two different people, one heading. */}
                <th className="px-3 py-2 text-left font-medium">
                  Dicocokkan oleh
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stroke-soft-200">
              {batch.orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  selected={selectedIds.has(order.id)}
                  onToggleSelect={() => onToggleOrder(order.id)}
                  onMatch={onMatchOrder}
                  onFlag={onFlag}
                  onOpen={onOpenOrder}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Off: the queue is everything awaiting verification, whenever it completed. */
const NO_COMPLETED_RANGE: DateRangeValue = {
  preset: 'all',
  customStart: '',
  customEnd: '',
};

const CodFinancePanel: React.FC<{
  /** Membuka panel detail COD untuk satu shipment — dipasang di level halaman. */
  onOpenOrder?: (shipmentId: string) => void;
}> = ({ onOpenOrder }) => {
  const [data, setData] = useState<CodFinanceQueue>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(0);

  const [clientId, setClientId] = useState<number | undefined>();
  const [hubId, setHubId] = useState<number | undefined>();
  const [period, setPeriod] = useState<'week' | 'all'>('week');
  /**
   * The completion-date filter, the same control the board uses. A finding aid
   * like client and hub: orders completed outside it are dimmed by the server,
   * the batch and its total stay whole.
   */
  const [completed, setCompleted] =
    useState<DateRangeValue>(NO_COMPLETED_RANGE);
  const { startDate: completedStartDate, endDate: completedEndDate } =
    rangeForPreset(completed.preset, {
      start: completed.customStart,
      end: completed.customEnd,
    });
  const [dimension, setDimension] = useState<'client' | 'hub'>('client');

  // Keyed by ORDER id, not by deposit key. The deposit checkbox is now a
  // select-all over these, which is the only way one state can serve both "tick
  // the whole transfer" and "tick three of its five orders".
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Tracks what is FOLDED, so the default (an empty map) is everything open —
  // see BatchCard. Keyed the other way round this would need seeding on every
  // fetch, and a batch arriving after one would silently come back collapsed.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Masterdata for the two pickers. `true` keeps the provider list loaded —
  // this is a page-level filter bar, not a modal that resets on close.
  const providers = useProvidersDropdown(true);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [hubsFailed, setHubsFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setHubsLoading(true);
    setHubsFailed(false);
    hubsAPI
      .list()
      .then((res) => {
        if (!alive) return;
        // Defensive: this is core-service's shape, not ours. A proxy that
        // answers a 404 with an HTML body still resolves, and `.map` on that
        // is what turns a missing service into a blank screen.
        setHubs(Array.isArray(res) ? res : []);
      })
      // No toast. A hub list that will not load leaves the filter on "Semua
      // hub"; interrupting Finance with a masterdata error while they are
      // reconciling money helps nobody. The picker says so itself instead.
      .catch(() => alive && setHubsFailed(true))
      .finally(() => alive && setHubsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const [matching, setMatching] = useState<CodFinanceSelection | null>(null);
  const [confirming, setConfirming] = useState<CodFinanceSelection | null>(
    null
  );
  const [flagging, setFlagging] = useState<CodFinanceOrder | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getCodFinanceQueue({
      clientId,
      hubId,
      period,
      completedStartDate,
      completedEndDate,
    })
      .then((res) => alive && setData(res))
      .catch((error) => alive && toast.error((error as Error).message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [clientId, hubId, period, completedStartDate, completedEndDate, token]);

  const refresh = useCallback(() => {
    // Clear the selection too: the ids in it may no longer be eligible, and a
    // stale tick on a terminal action is exactly what must not happen.
    setSelected({});
    setToken((t) => t + 1);
  }, []);

  const breakdown =
    dimension === 'client' ? data.breakdownByClient : data.breakdownByHub;

  /** Ticked order ids, as a Set — the cards ask membership once per row. */
  const selectedIds = useMemo(
    () => new Set(Object.keys(selected).filter((id) => selected[id])),
    [selected]
  );

  /**
   * The deposits the active filters leave on screen: those with at least one
   * order inside them. A filter decides WHICH transfers appear, never what is
   * inside one — a shown deposit keeps every order and its full total, with the
   * ones outside the filter dimmed. Without a filter every order is visible,
   * so this is the whole page.
   *
   * Before this, a deposit with nothing in the filter still showed at full
   * value ("menampilkan 0 dari 4"), so narrowing by completion date changed
   * which rows were dimmed and nothing else — it read as a filter that did not
   * work.
   */
  const shownBatches = useMemo(
    () => data.batches.filter((b) => b.visibleOrders > 0),
    [data.batches]
  );
  const hiddenBatches = data.batches.length - shownBatches.length;
  // Whole-transfer totals of what is shown — each one a real statement line.
  // Equal to the queue total when nothing is filtered.
  const shownAmount = shownBatches.reduce((sum, b) => sum + b.totalAmount, 0);

  /**
   * The current selection, resolved back to live rows.
   *
   * Rebuilt from `data` on every render rather than stored: a refresh replaces
   * the queue, and a selection holding the objects it was made from would act
   * on figures the screen no longer shows. Ids survive a refetch; rows do not.
   * Resolved against the SHOWN deposits only, for the same reason: an order
   * ticked before a filter hid its deposit must not ride along in a bulk
   * action nobody can see it in.
   */
  const selection = useMemo<CodFinanceSelection>(() => {
    const batches = shownBatches.filter((b) =>
      b.orders.some((o) => selected[o.id])
    );
    const orders = batches.flatMap((b) =>
      b.orders.filter((o) => selected[o.id])
    );
    // Whole = every pickable order of every batch involved. Flagged ones can
    // never be ticked, so they do not count against it — a deposit with one
    // flagged order is still "whole" when the rest are selected.
    const whole = batches.every((b) =>
      b.orders.filter((o) => !o.financeIssueReason).every((o) => selected[o.id])
    );
    return { orders, batches, whole };
  }, [shownBatches, selected]);

  // What a bulk CONFIRM would move: already matched and unflagged.
  const selectedConfirmable = selection.orders.filter((o) => o.eligible);
  // What a bulk MATCH would move: unflagged, matched or not. An order can be
  // eligible for one and not the other, which is why the two buttons carry
  // separate counts.
  const selectedMatchable = selection.orders.filter(
    (o) => !o.financeIssueReason
  );
  const selectedAmount = selectedConfirmable.reduce(
    (sum, o) => sum + o.depositedAmount,
    0
  );
  // What the operator actually picked, matched or not — the figure the sticky
  // bar leads with. `selectedAmount` above is the confirmable subset, and on
  // an unmatched selection that is zero, which is true but is not what the
  // person looking at two deposits worth Rp 9,6jt just did.
  const selectedTotal = selection.orders.reduce(
    (sum, o) => sum + o.depositedAmount,
    0
  );

  /** Tick or clear every pickable order in one deposit. */
  const toggleBatch = useCallback(
    (batch: CodFinanceDepositBatch, on: boolean) =>
      setSelected((prev) => {
        const next = { ...prev };
        for (const order of batch.orders) {
          if (order.financeIssueReason) continue;
          if (on) next[order.id] = true;
          else delete next[order.id];
        }
        return next;
      }),
    []
  );

  const toggleOrder = useCallback(
    (orderId: string) =>
      setSelected((prev) => {
        const next = { ...prev };
        if (next[orderId]) delete next[orderId];
        else next[orderId] = true;
        return next;
      }),
    []
  );

  /** One deposit, whole — what the card's own two buttons act on. */
  const wholeBatch = (batch: CodFinanceDepositBatch): CodFinanceSelection => ({
    orders: batch.orders,
    batches: [batch],
    whole: true,
  });

  /** One order — what a row's own Cocokkan acts on. */
  const oneOrder = (
    batch: CodFinanceDepositBatch,
    order: CodFinanceOrder
  ): CodFinanceSelection => ({
    orders: [order],
    batches: [batch],
    whole: batch.orders.filter((o) => !o.financeIssueReason).length === 1,
  });

  const onBreakdownRow = (row: CodFinanceBreakdownRow) => {
    // Clicking a breakdown row filters the list to it — the table doubles as
    // navigation, which is what keeps it useful past a handful of entities.
    //
    // `none` is the orders with no hub, and it is a REAL value to filter on —
    // not the absence of a filter. Mapping it to `undefined` made that one row
    // the only unclickable row in the table, which matters more than it sounds:
    // since 9 September every non-dispatch upload lands without a hub, so
    // "Tanpa hub" is where most of the queue now sits.
    if (dimension === 'client') {
      const id = Number(row.key);
      setClientId(clientId === id ? undefined : id);
      return;
    }
    const id = row.key === 'none' ? NO_HUB : Number(row.key);
    setHubId(hubId === id ? undefined : id);
  };

  const filtersOn =
    clientId !== undefined ||
    hubId !== undefined ||
    completed.preset !== NO_COMPLETED_RANGE.preset;

  // Both masterdata lists are guarded before they are mapped. `options` comes
  // from another service through a dev proxy, and when that service is down the
  // proxy can answer with something that is not an array — at which point
  // `.map` throws during render and takes the page with it. An empty picker is
  // a degraded filter; a thrown render is a lost screen.
  const providerOptions = useMemo(
    () =>
      (Array.isArray(providers.options) ? providers.options : []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    [providers.options]
  );

  const hubOptions = useMemo(
    () => [
      // A real option, not the absence of one: since the hub stopped being
      // captured on non-dispatch uploads, this is where most of the queue sits.
      { value: String(NO_HUB), label: 'Tanpa hub' },
      ...(Array.isArray(hubs) ? hubs : []).map((h) => ({
        value: String(h.id),
        label: h.name,
      })),
    ],
    [hubs]
  );

  return (
    <div className="flex flex-col gap-5 pb-24">
      {/* Above the tiles, not inside them: this is the one condition that means
          nobody is working the queue, and a tile among tiles reads as just
          another number to scan past. */}
      {data.kpi.staleOrders > 0 && (
        <Alert status="error" size="lg">
          <b>{data.kpi.staleOrders} setoran</b> belum terverifikasi lebih dari 7
          hari ({formatRupiah(data.kpi.staleAmount)}). Yang tertua sudah{' '}
          <b>{data.kpi.oldestDepositAgeDays} hari</b> — kemungkinan besar
          pengecekan mutasi belum dijalankan, bukan setorannya yang bermasalah.
        </Alert>
      )}

      {/* Four across from 1024 (`lg`), not 1280 (`xl`). 1024x720 is the
          smallest desktop we support, and there the 2x2 block cost ~115px of
          height on a viewport that only has ~600 — enough to push the deposit
          list, the actual work, below the fold. Measured before moving it: at
          1024 with the rail open each card gets 163px of content, against a
          widest figure of 145px (Rp 194.294.180) and a widest label of 139px.
          Both fit; nothing new truncates. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          icon="ri-time-line"
          label="Menunggu verifikasi"
          value={formatRupiah(data.kpi.awaitingAmount)}
          hint={`${data.kpi.awaitingOrders} pesanan · ${data.kpi.awaitingDeposits} setoran`}
          tone="warning"
        />
        <Kpi
          icon="ri-links-line"
          label="Sudah dicocokkan"
          value={String(data.kpi.matchedOrders)}
          hint="siap dikonfirmasi"
        />
        <Kpi
          icon="ri-shield-check-line"
          label="Confirmed by Finance"
          value={String(data.kpi.confirmedOrders)}
          hint={`${formatRupiah(data.kpi.confirmedAmount)} · ${
            period === 'week' ? '7 hari terakhir' : 'semua'
          }`}
          tone="success"
        />
        <Kpi
          icon="ri-error-warning-line"
          label="Bermasalah"
          value={String(data.kpi.flaggedOrders)}
          hint="perlu cleansing"
          tone={data.kpi.flaggedOrders > 0 ? 'error' : undefined}
        />
      </div>

      {/* One card, header and all — the prototype's "Ringkasan posisi". The
          dimension toggle belongs INSIDE it because it changes what this table
          shows and nothing else; floating above it, the same control read like
          a page-level filter. */}
      <div className="overflow-hidden rounded-xl border border-stroke-soft-200 bg-bg-white-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 px-4 py-3">
          <div className="text-label-medium">Ringkasan posisi</div>
          <SegmentedControl
            value={dimension}
            onValueChange={(v: string) =>
              v && setDimension(v as 'client' | 'hub')
            }
            size="sm"
          >
            <SegmentedItem value="client">Per client</SegmentedItem>
            <SegmentedItem value="hub">Per hub</SegmentedItem>
          </SegmentedControl>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-xs">
            <thead>
              {/* White, not tinted: on a white page a grey header strip made
                  the card read as a disabled panel. The hairline below carries
                  the separation instead. */}
              <tr className="border-b border-stroke-soft-200 bg-bg-white-0 text-text-soft-400">
                <th className="px-4 py-2.5 text-left font-medium">
                  {dimension === 'client' ? 'Client' : 'Hub'}
                </th>
                <th className="px-4 py-2.5 text-right font-medium">
                  Menunggu verifikasi
                </th>
                <th className="px-4 py-2.5 text-right font-medium">Pesanan</th>
                <th className="px-4 py-2.5 text-right font-medium">Cocok</th>
                <th className="px-4 py-2.5 text-right font-medium">
                  Bermasalah
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stroke-soft-200">
              {breakdown.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-text-soft-400"
                  >
                    {loading
                      ? 'Memuat…'
                      : 'Tidak ada setoran menunggu verifikasi'}
                  </td>
                </tr>
              ) : (
                breakdown.map((row) => {
                  const active =
                    dimension === 'client'
                      ? clientId === Number(row.key)
                      : hubId ===
                        (row.key === 'none' ? NO_HUB : Number(row.key));
                  return (
                    <tr
                      key={row.key}
                      onClick={() => onBreakdownRow(row)}
                      className={`cursor-pointer hover:bg-bg-weak-50 ${
                        // The accent rail marks the selected row the way the
                        // sidebar marks the active page — same vocabulary.
                        active
                          ? 'bg-primary-alpha-10 font-medium shadow-[inset_3px_0_0_var(--theme-accent-base)]'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-2.5">{row.label}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {formatRupiah(row.awaitingAmount)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {row.orders}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {row.matchedOrders}/{row.orders}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {row.flaggedOrders > 0 ? (
                          <span className="text-error-base">
                            {row.flaggedOrders}
                          </span>
                        ) : (
                          <span className="text-text-soft-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-stroke-soft-200 px-4 py-2.5 text-xs text-text-sub-600">
          Klik baris untuk memfilter. Filter meredupkan pesanan di luar pilihan
          — nilai setoran tetap utuh, karena satu setoran adalah satu baris
          mutasi bank.
        </div>
      </div>

      {/* Finance's own filter bar. Client and hub are real pickers fed by the
          same masterdata the rest of the console uses, not a list derived from
          whatever happens to be in the queue — a hub with nothing waiting has
          to be selectable, otherwise "no deposits from Bekasi" is
          indistinguishable from "Bekasi is not an option". */}
      <DateRangeFilter
        label="Tanggal selesai"
        value={completed}
        onChange={(next) => setCompleted((prev) => ({ ...prev, ...next }))}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-56">
          <SearchSelect
            options={providerOptions}
            value={clientId != null ? String(clientId) : undefined}
            selectedLabelFallback={
              clientId != null ? providers.labelFor(clientId) : undefined
            }
            onChange={(next) => setClientId(next ? Number(next) : undefined)}
            // Short enough to FIT the w-56 trigger. "Daftar client tidak
            // termuat" clipped to "Daftar client tidak termu…" — an error
            // message the operator cannot finish reading is worse than none,
            // because they cannot tell a broken picker from an empty one. The
            // full explanation lives once under the row.
            placeholder={
              providers.error ? 'Client tidak tersedia' : 'Semua client'
            }
            allowClear
            loading={providers.loading}
            hasMore={providers.hasMore}
            disableLocalFilter
            onSearchChange={providers.setSearch}
            onLoadMore={providers.loadMore}
          />
        </div>

        <div className="w-56">
          <SearchSelect
            options={hubOptions}
            value={hubId != null ? String(hubId) : undefined}
            onChange={(next) => setHubId(next ? Number(next) : undefined)}
            placeholder={hubsFailed ? 'Hub tidak tersedia' : 'Semua hub'}
            allowClear
            loading={hubsLoading}
          />
        </div>

        <SegmentedControl
          value={period}
          onValueChange={(v: string) => v && setPeriod(v as 'week' | 'all')}
          size="sm"
        >
          <SegmentedItem value="week">Minggu ini</SegmentedItem>
          <SegmentedItem value="all">Semua</SegmentedItem>
        </SegmentedControl>

        {filtersOn && (
          <Button
            style="ghost"
            size="sm"
            onClick={() => {
              setClientId(undefined);
              setHubId(undefined);
              setCompleted(NO_COMPLETED_RANGE);
            }}
          >
            Reset filter
          </Button>
        )}

        {/* Exports what is on screen, no second fetch: the queue is already in
            hand, and a re-fetch would risk handing the operator a file that
            disagrees with the page they are looking at. */}
        <Button
          style="stroke"
          size="sm"
          className="ml-auto"
          disabled={shownBatches.length === 0}
          onClick={() =>
            downloadCodFinanceCsv({ ...data, batches: shownBatches })
          }
        >
          <i className="ri-download-2-line" />
          Export CSV
        </Button>
      </div>

      {/* One line for both pickers, BELOW the row rather than under the hub
          select. Inside the flex it grew that one cell and pushed the client
          select out of line with it; and only hub had it, so a client outage
          — the same event — explained itself nowhere. Naming which masterdata
          is down matters: the operator's next question is whether the queue is
          also short, and the answer is no. */}
      {(providers.error || hubsFailed) && (
        <p className="-mt-1 text-[11px] text-text-soft-400">
          Masterdata{' '}
          {providers.error && hubsFailed
            ? 'client dan hub'
            : providers.error
              ? 'client'
              : 'hub'}{' '}
          tidak tersedia — filter itu nonaktif. Antrian di bawah tetap lengkap.
        </p>
      )}

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-label-medium">
          Setoran menunggu verifikasi — {shownBatches.length} setoran ·{' '}
          {formatRupiah(shownAmount)}
          {hiddenBatches > 0 && (
            <span className="ml-1.5 text-xs font-normal text-text-soft-400">
              · {hiddenBatches} setoran lain disembunyikan filter
            </span>
          )}
        </div>
        {data.hasMore && (
          <div className="text-xs text-text-sub-600">
            menampilkan <b>{data.batches.length}</b> terlama dari{' '}
            <b>{data.totalDeposits}</b>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {shownBatches.length === 0 && !loading ? (
          <div className="rounded-xl border border-dashed border-stroke-soft-200 px-4 py-10 text-center text-sm text-text-soft-400">
            {data.batches.length === 0
              ? 'Semua setoran sudah diverifikasi Finance.'
              : 'Tidak ada setoran dengan pesanan yang cocok dengan filter.'}
          </div>
        ) : (
          shownBatches.map((batch) => (
            <BatchCard
              key={batch.key}
              batch={batch}
              selectedIds={selectedIds}
              expanded={!collapsed[batch.key]}
              onToggleAll={(on) => toggleBatch(batch, on)}
              onToggleOrder={toggleOrder}
              onToggleExpand={() =>
                setCollapsed((c) => ({ ...c, [batch.key]: !c[batch.key] }))
              }
              onMatch={() => setMatching(wholeBatch(batch))}
              onMatchOrder={(order) => setMatching(oneOrder(batch, order))}
              onConfirm={() => setConfirming(wholeBatch(batch))}
              onFlag={setFlagging}
              onOpenOrder={(order) => onOpenOrder?.(order.id)}
            />
          ))
        )}
      </div>

      {/* Sticky, because the selection is built by scrolling and the action has
          to stay reachable without scrolling back. Inverted to ink, like the
          prototype: this bar is a MODE — a selection is pending and one button
          on it is irreversible — and a white bar on a white page reads as one
          more row of content to scroll past. */}
      {selection.orders.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 bg-bg-strong-950 text-text-white-0 shadow-[0_-6px_24px_rgba(23,23,23,.22)]">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-6 py-3">
            {/* Says what is SELECTED first, then what is confirmable — in that
                order, because the operator just picked the deposits and the
                first number should be the one they recognise. It used to lead
                with the confirmable subset, so selecting two unmatched
                deposits worth Rp 9.649.226 printed "0 pesanan cocok · Rp 0"
                and read like the selection had failed. */}
            <div className="text-sm">
              <b>{selection.orders.length} pesanan</b> dipilih
              {selection.batches.length > 1 && (
                <> dari {selection.batches.length} setoran</>
              )}{' '}
              · <b>{formatRupiah(selectedTotal)}</b>
              {selectedConfirmable.length > 0 ? (
                <>
                  {' '}
                  · <b>{selectedConfirmable.length} siap dikonfirmasi</b> (
                  {formatRupiah(selectedAmount)})
                </>
              ) : (
                <span className="text-white/70">
                  {' '}
                  · belum ada yang cocok — cocokkan dulu dengan mutasi
                </span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelected({})}
                className="h-9 rounded-sm border border-white/30 px-3 text-sm font-medium text-text-white-0 hover:bg-white/10"
              >
                Batal pilih
              </button>
              {/* Matching in bulk, not only confirming. One bank line often
                  covers several PIC transfers, and without this the operator
                  had to open each deposit and retype the same reference —
                  which is also how two of them end up spelled differently and
                  stop looking like one statement line. */}
              <button
                type="button"
                onClick={() => setMatching(selection)}
                disabled={selectedMatchable.length === 0}
                className="h-9 rounded-sm border border-white/30 px-3 text-sm font-medium text-text-white-0 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cocokkan dgn mutasi ({selectedMatchable.length})
              </button>
              <button
                type="button"
                onClick={() => setConfirming(selection)}
                disabled={selectedConfirmable.length === 0}
                className="h-9 rounded-sm bg-bg-white-0 px-3 text-sm font-medium text-text-strong-950 hover:bg-bg-weak-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirmed by Finance ({selectedConfirmable.length})
              </button>
            </div>
          </div>
        </div>
      )}

      <MatchDepositModal
        selection={matching}
        onClose={() => setMatching(null)}
        onDone={refresh}
      />
      <ConfirmFinanceModal
        selection={confirming}
        onClose={() => setConfirming(null)}
        onDone={refresh}
      />
      <FinanceIssueModal
        order={flagging}
        onClose={() => setFlagging(null)}
        onDone={refresh}
      />
    </div>
  );
};

export default CodFinancePanel;
